/**
 * Shared FilterBar component — Slate Industrial theme
 * Usage: <FilterBar filters={...} onChange={...} config={...} onClear={...} />
 */
import { useState } from 'react';
import { ChevronDown, X, SlidersHorizontal } from 'lucide-react';

// Generic select filter
export function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <label className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        data-testid={testId}
        className="h-8 px-2 rounded-md font-['IBM_Plex_Sans'] text-xs focus:outline-none transition-colors min-w-[120px]"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
      >
        <option value="">All</option>
        {options.map(o => (
          <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
        ))}
      </select>
    </div>
  );
}

// Generic text input filter
export function FilterInput({ label, value, onChange, placeholder, testId }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <label className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ''}
        data-testid={testId}
        className="h-8 px-2 rounded-md font-['IBM_Plex_Sans'] text-xs focus:outline-none transition-colors min-w-[140px]"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
      />
    </div>
  );
}

// Date input
export function FilterDate({ label, value, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>{label}</label>
      <input
        type="date"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="h-8 px-2 rounded-md font-['IBM_Plex_Sans'] text-xs focus:outline-none transition-colors w-[130px]"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
      />
    </div>
  );
}

// Active filter count badge
export function ActiveFilterCount({ filters, excludeKeys = [] }) {
  const count = Object.entries(filters)
    .filter(([k, v]) => !excludeKeys.includes(k) && v !== '' && v !== null && v !== undefined)
    .length;
  if (count === 0) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold font-['JetBrains_Mono']"
      style={{ background: 'var(--primary-muted)', color: 'var(--primary)', border: '1px solid var(--primary)' }}>
      {count} active
    </span>
  );
}

// Filter panel wrapper
export default function FilterBar({ children, onClear, activeCount, className = '' }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className={`card-slate ${className}`}>
      <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={12} style={{ color: 'var(--text-faint)' }} />
          <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>Filters</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold font-['JetBrains_Mono']"
              style={{ background: 'var(--primary-muted)', color: 'var(--primary)', border: '1px solid var(--primary)' }}>
              {activeCount} active
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {activeCount > 0 && (
            <button onClick={onClear} className="flex items-center gap-1 text-[10px] font-['JetBrains_Mono'] transition-colors"
              style={{ color: 'var(--text-faint)' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--error)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}>
              <X size={10} /> CLEAR ALL
            </button>
          )}
          <button onClick={() => setExpanded(v => !v)} className="transition-colors"
            style={{ color: 'var(--text-faint)' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}>
            <ChevronDown size={14} className={`transition-transform ${expanded ? '' : '-rotate-90'}`} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="p-4 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
}
